import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { usersApi } from '@/api/users';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { humanizeError } from '@/lib/errors';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { IS_APP_BUILD } from '@/lib/appBrand';

type AdminStats = {
  active_students: number;
  total_students: number;
  completed_lessons: number;
  revenue_total: number;
  pending_homework: number;
  avg_homework_review_time_hours: number;
  approval_rate: number; // 0..1
};

type StudentRank = {
  rank: number;
  student_id: string;
  student_name?: string;
  total_points: number;
  completed_lessons: number;
  current_streak: number;
};

type CuratorEfficiency = {
  curator_id: string;
  curator_name?: string;
  reviewed: number;
  approved: number;
  approval_rate: number; // 0..1
  avg_review_time_hours: number;
};

type AttendanceByTariff = {
  tariff_plan_id: string;
  tariff_title?: string;
  total_marks: number;
  present_marks: number;
  attendance_rate: number; // 0..1
};

type MarathonAnalytics = {
  marathon_id: string;
  title: string;
  enrolled_students: number;
  total_lessons: number;
  completed_lessons: number;
  completion_rate: number; // 0..1
};

const pct = (v?: number) => (v == null ? '—' : `${Math.round(v * 100)}%`);

// Часы с защитой от отсутствующего поля в ответе бэкенда.
const hours = (v?: number) => (typeof v === 'number' ? `${v.toFixed(1)}ч` : '—');

export function AdminStatsPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const stats = useQuery({
    queryKey: ['admin', 'analytics', 'summary'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/admin/analytics/summary');
        return data as AdminStats;
      } catch {
        return null;
      }
    },
  });

  const ranking = useQuery({
    queryKey: ['admin', 'analytics', 'student-ranking'],
    queryFn: async () => {
      const { data } = await api.get('/admin/analytics/student-ranking', { params: { limit: 20 } });
      return (data?.items ?? []) as StudentRank[];
    },
  });

  const curators = useQuery({
    queryKey: ['admin', 'analytics', 'curators'],
    queryFn: async () => {
      const { data } = await api.get('/admin/analytics/curators');
      return (data?.items ?? []) as CuratorEfficiency[];
    },
  });

  const attendance = useQuery({
    queryKey: ['admin', 'analytics', 'attendance'],
    queryFn: async () => {
      const { data } = await api.get('/admin/analytics/attendance');
      return (data?.items ?? []) as AttendanceByTariff[];
    },
  });

  const marathons = useQuery({
    queryKey: ['admin', 'analytics', 'marathons'],
    queryFn: async () => {
      const { data } = await api.get('/admin/analytics/marathons');
      return (data?.items ?? []) as MarathonAnalytics[];
    },
  });

  const studentsCount = useQuery({
    queryKey: ['admin', 'stats-students'],
    queryFn: () => usersApi.list({ role: 'student', limit: 1 }),
  });

  const exportCSV = async (dataset = 'summary') => {
    try {
      const resp = await api.get('/admin/analytics/export', {
        params: { dataset },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onebilim-analytics-${dataset}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      void ask({ message: humanizeError(e), notice: true });
    }
  };

  return (
    <>
      <PageHeader
        title={t('adminStats.title')}
        subtitle={t('adminStats.subtitle')}
        actions={
          <Button variant="secondary" onClick={() => exportCSV('summary')}>
            <span className="material-symbols-outlined">download</span>{t('adminStats.exportCsv')}
          </Button>
        }
      />

      <section className="grid gap-3 md:grid-cols-3">
        <StatTile
          label={t('adminStats.activeStudents')}
          value={stats.data?.active_students ?? studentsCount.data?.total ?? '—'}
          icon="groups"
        />
        <StatTile label={t('adminStats.completedLessons')} value={stats.data?.completed_lessons ?? '—'} icon="check_circle" />
        <StatTile label={t('adminStats.pendingHomework')} value={stats.data?.pending_homework ?? '—'} icon="assignment_late" />
        <StatTile
          label={t('adminStats.hwReviewSpeed')}
          value={hours(stats.data?.avg_homework_review_time_hours)}
          icon="schedule"
        />
        <StatTile label={t('adminStats.approvalRate')} value={pct(stats.data?.approval_rate)} icon="thumb_up" />
      </section>

      {/* Student ranking by points */}
      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display">{t('adminStats.studentRanking')}</h2>
          <Button variant="ghost" onClick={() => exportCSV('students')}>
            <span className="material-symbols-outlined">download</span>CSV
          </Button>
        </div>
        {ranking.data?.length ? (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-outline-variant/20">
                <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                  <th className="p-3">#</th>
                  <th className="p-3">{t('adminStats.colStudent')}</th>
                  <th className="p-3">{t('adminStats.colPoints')}</th>
                  <th className="p-3">{t('adminStats.colLessons')}</th>
                  <th className="p-3">{t('adminStats.colStreak')}</th>
                </tr>
              </thead>
              <tbody>
                {ranking.data.map((r) => (
                  <tr key={r.student_id} className="border-b border-outline-variant/20 last:border-0">
                    <td className="p-3 font-semibold">{r.rank}</td>
                    <td className="p-3">{r.student_name || t('adminStats.noName')}</td>
                    <td className="p-3 font-semibold">{r.total_points}</td>
                    <td className="p-3">{r.completed_lessons}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1">
                        {r.current_streak}
                        <span className="material-symbols-outlined text-base text-warning">local_fire_department</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
          <TableState
            isLoading={ranking.isLoading}
            isError={ranking.isError}
            error={ranking.error}
            empty={t('adminStats.rankingEmpty')}
          />
        )}
      </section>

      {/* Curator efficiency */}
      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display">{t('adminStats.curatorEfficiency')}</h2>
          <Button variant="ghost" onClick={() => exportCSV('curators')}>
            <span className="material-symbols-outlined">download</span>CSV
          </Button>
        </div>
        {curators.data?.length ? (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-outline-variant/20">
                <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                  <th className="p-3">{t('adminStats.colCurator')}</th>
                  <th className="p-3">{t('adminStats.colReviewed')}</th>
                  <th className="p-3">{t('adminStats.colApproved')}</th>
                  <th className="p-3">{t('adminStats.colApprovalRate')}</th>
                  <th className="p-3">{t('adminStats.colAvgTime')}</th>
                </tr>
              </thead>
              <tbody>
                {curators.data.map((cur) => (
                  <tr key={cur.curator_id} className="border-b border-outline-variant/20 last:border-0">
                    <td className="p-3 font-semibold">{cur.curator_name || t('adminStats.noName')}</td>
                    <td className="p-3">{cur.reviewed}</td>
                    <td className="p-3">{cur.approved}</td>
                    <td className="p-3">{pct(cur.approval_rate)}</td>
                    <td className="p-3">{hours(cur.avg_review_time_hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
          <TableState
            isLoading={curators.isLoading}
            isError={curators.isError}
            error={curators.error}
            empty={t('adminStats.curatorEfficiencyEmpty')}
          />
        )}
      </section>

      {/* Посещаемость по тарифам — только сайт (решение владельца 13.10.2026: в приложении ни слова «тариф»). */}
      {!IS_APP_BUILD && (
      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display">{t('adminStats.attendanceByTariff')}</h2>
          <Button variant="ghost" onClick={() => exportCSV('attendance')}>
            <span className="material-symbols-outlined">download</span>CSV
          </Button>
        </div>
        {attendance.data?.length ? (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-outline-variant/20">
                <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                  <th className="p-3">{t('adminStats.colTariff')}</th>
                  <th className="p-3">{t('adminStats.colMarks')}</th>
                  <th className="p-3">{t('adminStats.colPresent')}</th>
                  <th className="p-3">{t('adminStats.colAttendance')}</th>
                </tr>
              </thead>
              <tbody>
                {attendance.data.map((a) => (
                  <tr key={a.tariff_plan_id} className="border-b border-outline-variant/20 last:border-0">
                    <td className="p-3 font-semibold">{a.tariff_title || t('adminStats.noTitle')}</td>
                    <td className="p-3">{a.total_marks}</td>
                    <td className="p-3">{a.present_marks}</td>
                    <td className="p-3"><Pill variant="primary">{pct(a.attendance_rate)}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
          <TableState
            isLoading={attendance.isLoading}
            isError={attendance.isError}
            error={attendance.error}
            empty={t('adminStats.attendanceEmpty')}
          />
        )}
      </section>
      )}

      {/* Marathon analytics */}
      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display">{t('adminStats.byMarathons')}</h2>
          <Button variant="ghost" onClick={() => exportCSV('marathons')}>
            <span className="material-symbols-outlined">download</span>CSV
          </Button>
        </div>
        {marathons.data?.length ? (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-outline-variant/20">
                <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                  <th className="p-3">{t('adminStats.colMarathon')}</th>
                  <th className="p-3">{t('adminStats.colStudents')}</th>
                  <th className="p-3">{t('adminStats.colTotalLessons')}</th>
                  <th className="p-3">{t('adminStats.colCompleted')}</th>
                  <th className="p-3">{t('adminStats.colCompletionRate')}</th>
                </tr>
              </thead>
              <tbody>
                {marathons.data.map((m) => (
                  <tr key={m.marathon_id} className="border-b border-outline-variant/20 last:border-0">
                    <td className="p-3 font-semibold">{m.title}</td>
                    <td className="p-3">{m.enrolled_students}</td>
                    <td className="p-3">{m.total_lessons}</td>
                    <td className="p-3">{m.completed_lessons}</td>
                    <td className="p-3"><Pill variant="success">{pct(m.completion_rate)}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : (
          <TableState
            isLoading={marathons.isLoading}
            isError={marathons.isError}
            error={marathons.error}
            empty={t('adminStats.marathonsEmpty')}
          />
        )}
      </section>
    </>
  );
}

// Единый плейсхолдер для таблиц: различает загрузку, ошибку и пустой список.
function TableState({
  isLoading,
  isError,
  error,
  empty,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  empty: string;
}) {
  const { t } = useTranslation();
  if (isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (isError)
    return (
      <Card variant="subtle" className="text-on-surface-variant">
        {humanizeError(error)}
      </Card>
    );
  return (
    <Card variant="subtle" className="text-on-surface-variant">
      {empty}
    </Card>
  );
}

function StatTile({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <Card>
      <CardTitle>{label}</CardTitle>
      <div className="mt-2 flex items-center gap-3">
        <span className="material-symbols-outlined text-3xl text-primary">{icon}</span>
        <p className="font-display text-3xl font-extrabold tracking-display">{value}</p>
      </div>
    </Card>
  );
}
